// A drop-in replacement for `<input type="datetime-local">` /
// `<input type="date">` whose calendar popover we fully control — because
// the native picker's first-day-of-week and display format are locked to
// the browser locale (no JS/CSS lever). We need Monday-first WITH US
// MM/DD/YYYY + 12h formatting, which no single locale provides, so the
// calendar is hand-rolled here (Monday-first, matching the Calendar
// feature's `weekStartsOn: 1` grid) while the value contract stays exactly
// the native one: a local wall-clock string, `YYYY-MM-DDTHH:mm` in
// datetime mode or `YYYY-MM-DD` in date mode (empty string = unset). That
// keeps every call site's UTC<->local conversion unchanged.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

export type DateTimeFieldMode = 'datetime' | 'date';

interface DateTimeFieldProps {
  /** Local wall-clock value: `YYYY-MM-DDTHH:mm` (datetime) or `YYYY-MM-DD`
   *  (date). Empty string means unset. Same contract as the native input. */
  value: string;
  onChange: (value: string) => void;
  mode?: DateTimeFieldMode;
  id?: string;
  className?: string;
  ariaLabel?: string;
  /** Open the calendar immediately on mount (used when the field is
   *  revealed by an edit action that already had a user gesture). */
  autoOpen?: boolean;
  disabled?: boolean;
  /** Time (HH:mm) applied when a day is picked on a previously-empty
   *  datetime field. Defaults to 10:00, matching the app's default due hour. */
  defaultTime?: string;
}

interface Parts {
  y: number;
  mo: number; // 1-12
  d: number;
  hh: number;
  mm: number;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Parse the local wall-clock string into integer parts, or null when
 *  unset/malformed. Parsed field-by-field (not via Date) so no timezone
 *  reinterpretation can shift the calendar day. */
function parseValue(value: string, mode: DateTimeFieldMode): Parts | null {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  if (!y || !mo || !d) return null;
  let hh = 0;
  let mm = 0;
  if (mode === 'datetime' && timePart) {
    const [h, m] = timePart.split(':').map(Number);
    hh = Number.isFinite(h) ? h : 0;
    mm = Number.isFinite(m) ? m : 0;
  }
  return { y, mo, d, hh, mm };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function serialize(p: Parts, mode: DateTimeFieldMode): string {
  const date = `${p.y}-${pad(p.mo)}-${pad(p.d)}`;
  return mode === 'date' ? date : `${date}T${pad(p.hh)}:${pad(p.mm)}`;
}

/** US-format display: MM/DD/YYYY and, for datetime, h:mm AM/PM. Formatted
 *  manually so the browser locale can't flip it to DD/MM or 24h. */
function formatDisplay(p: Parts | null, mode: DateTimeFieldMode): string {
  if (!p) return '';
  const date = `${pad(p.mo)}/${pad(p.d)}/${p.y}`;
  if (mode === 'date') return date;
  const ampm = p.hh < 12 ? 'AM' : 'PM';
  const h12 = p.hh % 12 === 0 ? 12 : p.hh % 12;
  return `${date}, ${h12}:${pad(p.mm)} ${ampm}`;
}

export function DateTimeField({
  value,
  onChange,
  mode = 'datetime',
  id,
  className,
  ariaLabel,
  autoOpen = false,
  disabled = false,
  defaultTime = '10:00',
}: DateTimeFieldProps) {
  const parts = useMemo(() => parseValue(value, mode), [value, mode]);
  const [open, setOpen] = useState(autoOpen);
  // Month the grid is showing. Anchored on the selected day, else today.
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    parts ? new Date(parts.y, parts.mo - 1, parts.d) : new Date(),
  );
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Keep the visible month in sync when the value changes from outside
  // (e.g. the parent seeds a default due date on entering edit mode).
  useEffect(() => {
    if (parts) setViewMonth(new Date(parts.y, parts.mo - 1, parts.d));
  }, [parts]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const gridDays = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewMonth]);

  const selectedDate = parts
    ? new Date(parts.y, parts.mo - 1, parts.d)
    : null;

  const emitDay = useCallback(
    (day: Date) => {
      const [dh, dm] = defaultTime.split(':').map(Number);
      const next: Parts = {
        y: day.getFullYear(),
        mo: day.getMonth() + 1,
        d: day.getDate(),
        hh: parts ? parts.hh : (Number.isFinite(dh) ? dh : 10),
        mm: parts ? parts.mm : (Number.isFinite(dm) ? dm : 0),
      };
      onChange(serialize(next, mode));
      if (mode === 'date') setOpen(false);
    },
    [defaultTime, mode, onChange, parts],
  );

  const emitTime = useCallback(
    (timeStr: string) => {
      if (!timeStr) return;
      const [h, m] = timeStr.split(':').map(Number);
      const base = parts ?? {
        y: viewMonth.getFullYear(),
        mo: viewMonth.getMonth() + 1,
        d: viewMonth.getDate(),
        hh: 0,
        mm: 0,
      };
      onChange(serialize({ ...base, hh: h, mm: m }, mode));
    },
    [mode, onChange, parts, viewMonth],
  );

  const timeValue = parts ? `${pad(parts.hh)}:${pad(parts.mm)}` : '';
  const display = formatDisplay(parts, mode);

  return (
    <div className={`datetime-field${className ? ` ${className}` : ''}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="datetime-field__trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={display ? '' : 'datetime-field__placeholder'}>
          {display || (mode === 'date' ? 'Select date' : 'Select date & time')}
        </span>
      </button>

      {open && (
        <div className="datetime-field__popover" role="dialog" aria-label="Choose a date">
          <div className="datetime-field__nav">
            <button
              type="button"
              className="datetime-field__nav-btn"
              onClick={() => setViewMonth((m) => addMonths(m, -1))}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="datetime-field__month-label" aria-live="polite">
              {MONTH_LABELS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button
              type="button"
              className="datetime-field__nav-btn"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          <div className="datetime-field__weekdays" role="row">
            {WEEKDAY_LABELS.map((w) => (
              <span key={w} className="datetime-field__weekday" role="columnheader">
                {w}
              </span>
            ))}
          </div>

          <div className="datetime-field__grid" role="grid">
            {gridDays.map((day) => {
              const outside = !isSameMonth(day, viewMonth);
              const selected = selectedDate ? isSameDay(day, selectedDate) : false;
              const today = isSameDay(day, new Date());
              const cls = [
                'datetime-field__day',
                outside ? 'datetime-field__day--outside' : '',
                selected ? 'datetime-field__day--selected' : '',
                today && !selected ? 'datetime-field__day--today' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <button
                  type="button"
                  key={day.toISOString()}
                  className={cls}
                  onClick={() => emitDay(day)}
                  aria-pressed={selected}
                  aria-label={formatDisplay(
                    { y: day.getFullYear(), mo: day.getMonth() + 1, d: day.getDate(), hh: 0, mm: 0 },
                    'date',
                  )}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          {mode === 'datetime' && (
            <div className="datetime-field__time-row">
              <label className="datetime-field__time-label">
                Time
                <input
                  type="time"
                  className="datetime-field__time-input"
                  value={timeValue}
                  onChange={(e) => emitTime(e.target.value)}
                />
              </label>
            </div>
          )}

          <div className="datetime-field__actions">
            {value && (
              <button
                type="button"
                className="datetime-field__clear"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                Clear
              </button>
            )}
            <button
              type="button"
              className="datetime-field__done"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
